import ClipboardJS from "clipboard";



import { Factory } from "@/data/dao/Factory";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { TableDao } from "@/data/dao/service/TableDao";
import { PermalinkMode } from "@/data/model/Enums";
import type { IPermalink } from "@/data/model/Permalink";



import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";



import { i18n } from "@/portal/Trans";
import { UiNotification } from "@/portal/ui/Notification";


export class PermalinkGenerator {
    private $element: JQuery;
    private dao?: LocationDao | TableDao;
    private permaLink?: IPermalink;
    private entityId?: string;
    private clipboard?: ClipboardJS;

    // Used to generate QR for tables in a given application mode
    private mode: PermalinkMode = PermalinkMode.None;
    private isForTable: boolean = false;

    constructor(element: JQuery) {
        this.$element = element;
    }

    public loadForLocation(locationid: string): PermalinkGenerator {
        this.dao = Factory.getInstance<LocationDao>(LocationDao);
        this.isForTable = false;

        this.entityId = locationid;

        this.load();
        return this;
    }

    public loadForTable(tableId: string): PermalinkGenerator {
        this.dao = Factory.getInstance<TableDao>(TableDao);
        this.isForTable = true;
        this.entityId = tableId;

        this.load();
        return this;
    }

    public setMode(mode: PermalinkMode): PermalinkGenerator {
        this.mode = mode;
        if (this.permaLink !== undefined) this.setExisting();
        return this;
    }

    private load() {
        if (this.permaLink !== undefined) {
            this.setExisting();
            return;
        }

        this.$element.html(
            '<div class="loading"><div class="kt-spinner kt-spinner--lg kt-spinner--success"></div> ' +
                i18n("general.loading") +
                "</div>"
        );

        this.dao!.getPermalink(this.entityId!)
            .then((p) => {
                this.permaLink = p;
                if (this.permaLink !== undefined) this.setExisting();
                else this.setGenerateNew();
            })
            .catch((ex) => {
                logError(ex);
                this.setGenerateNew();
            });
    }

    private setExisting() {
        const port =
            location.port !== "" && location.port !== "80" && location.port !== "443" ? ":" + location.port : "";
        const tastierUrl = location.protocol + "//go." + location.hostname.split(".").slice(-2).join(".") + port;

        let link;
        if (this.permaLink!.locationSlug != null && this.permaLink!.tableIsVisibleOnline == true) {
            link = `${tastierUrl}/location/${this.permaLink!.locationSlug}/${this.permaLink!.code}`;
        } else {
            link = `${tastierUrl}/${this.permaLink!.code}`;
        }

        if (this.isForTable && this.mode !== PermalinkMode.None)
            link += this.mode === PermalinkMode.Kiosk ? "?mode=kiosk" : "?mode=normal";

        const permalink = $('<div class="permalink"><div class="input-group"></div></div>')
            .appendTo(this.$element.empty())
            .find("> .input-group");
        const input = $('<input type="text" class="form-control" value="' + link + '" readonly />').appendTo(permalink);
        const btnGroup = $('<div class="input-group-append" />').appendTo(permalink);

        const clipBtn = $('<a href="#" class="btn btn-secondary copy"><i class="la la-copy"></i></a>')
            .appendTo(btnGroup)
            .click((e) => {
                e.preventDefault();
            });

        $('<a href="#" class="btn btn-secondary qr"><i class="la la-qrcode"></i></a>')
            .appendTo(btnGroup)
            .click((e) => {
                e.preventDefault();
                this.dao!.downloadPermalinkQrCode(this.entityId!, this.mode);
            });

        const revokeBtn = $('<a href="#" class="btn btn-secondary remove"><i class="la la-trash"></i></a>')
            .appendTo(btnGroup)
            .click((e) => {
                e.preventDefault();

                UiNotification.confirm(i18n("table.permalink.reallyDelete"), undefined, () => {
                    revokeBtn
                        .addClass("kt-spinner kt-spinner--left kt-spinner--sm kt-spinner--success disabled")
                        .prop("disabled", true)
                        .find("> .la")
                        .remove();
                    this.dao!.removePermalink(this.entityId!, this.permaLink!.id)
                        .then(() => {
                            this.permaLink = undefined;
                            this.setGenerateNew();
                        })
                        .catch((ex) => {
                            logError(ex);
                            UiNotification.showException(i18n("table.permalink.failedDelete"));
                            this.setExisting();
                        });
                });
            });

        if (this.clipboard !== undefined) this.clipboard.destroy();

        this.clipboard = new ClipboardJS(clipBtn[0], {
            target: function () {
                return input[0];
            },
        });

        this.clipboard.on("success", (e) => {
            UiNotification.showSuccess(i18n("table.permalink.copiedClipboard"));
            e.clearSelection();
        });
    }

    private setGenerateNew() {
        const none = $('<div class="none" />').appendTo(this.$element.empty());
        const btn = $('<button class="btn btn-success">' + i18n("table.permalink.create") + "</button>")
            .appendTo(none)
            .click((e) => {
                e.preventDefault();
                e.stopPropagation();

                btn.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();
                this.dao!.createPermalink(this.entityId!)
                    .then((permal) => {
                        this.permaLink = permal;
                        this.setExisting();
                    })
                    .catch((ex) => {
                        logError(ex);
                        UiNotification.showException(i18n("table.permalink.failedCreate"));
                        this.setGenerateNew();
                    });
            });
    }
}
